import { useState, useEffect } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { calculateTaxValues } from '../../../utils/taxCalculator';
import { useCreditNoteLookup } from '../../../hooks/purchases/useCreditNoteLookup';
import SvgIcon from '../../SvgIcon/SvgIcon';

interface Props {
  onAdd: (product: any) => void;
}

const AddReturnProductForm = ({ onAdd }: Props) => {
  const {
    form: { supplier, id: creditNoteId },
  } = useSelector((state: RootState) => state.basePurchase);

  const {
    filteredDocuments,
    productOptions,
    searchEligiblePurchases,
    fetchProductsByPurchase,
    clearCreditNoteLookup,
  } = useCreditNoteLookup();

  const [form, setForm] = useState({
    purchaseRefId: '',
    productId: '',
    productName: '',
    unitCost: '',
    quantity: '',
    ivaTotal: '',
    totalCost: '',
    withIva: '',
    ivaUnit: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (supplier?.id) {
      searchEligiblePurchases({ supplierId: supplier.id });
    }
    return () => clearCreditNoteLookup();
  }, [supplier?.id]);

  useEffect(() => {
    const { unitCost, quantity } = form;
    if (unitCost && quantity && parseFloat(quantity) > 0) {
      const calc = calculateTaxValues(
        parseFloat(unitCost),
        parseFloat(quantity)
      );
      setForm((prev) => ({
        ...prev,
        ivaTotal: calc.ivaTotal.toString(),
        totalCost: calc.totalCost.toString(),
        withIva: calc.unitCostWithIva.toString(),
        ivaUnit: calc.ivaUnit.toString(),
      }));
    }
  }, [form.unitCost, form.quantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    const errs: typeof errors = {};
    if (!form.purchaseRefId) errs.purchaseRefId = 'CCF requerido';
    if (!form.productId) errs.productId = 'Producto requerido';
    if (!form.unitCost) errs.unitCost = 'Costo unitario requerido';
    if (!form.quantity) errs.quantity = 'Cantidad requerida';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const payload = {
      creditNoteId,
      purchaseReferenceId: Number(form.purchaseRefId),
      productId: Number(form.productId),
      quantity: Number(form.quantity),
      unitCost: Number(form.unitCost),
      type: 'RETURN',
    };

    onAdd(payload);
    resetForm();
  };

  const resetForm = () => {
    setForm({
      purchaseRefId: '',
      productId: '',
      productName: '',
      unitCost: '',
      quantity: '',
      ivaTotal: '',
      totalCost: '',
      withIva: '',
      ivaUnit: '',
    });
    setErrors({});
  };

  return (
    <div className="space-y-4">
      {/* Línea de CCF y producto */}
      {filteredDocuments.length === 0 && (
        <div className="flex items-start gap-2 rounded border border-yellow-300 bg-yellow-50 p-3 text-yellow-700">
          <SvgIcon name="info" className="mt-0.5 h-5 w-5 text-yellow-500" />
          <p className="text-sm">
            Este proveedor no tiene compras{' '}
            <strong>de tipo Crédito Fiscal</strong> disponibles para aplicar una{' '}
            <strong>nota de crédito</strong>.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <SimpleSearchSelect
            label="CCF del Proveedor"
            name="purchaseRefId"
            iconKey="invoice"
            options={filteredDocuments}
            selectedValue={
              form.purchaseRefId ? Number(form.purchaseRefId) : null
            }
            onSelect={(option: any) => {
              setForm((prev) => ({
                ...prev,
                purchaseRefId: option.id,
                productId: '',
                productName: '',
                unitCost: '',
              }));
              fetchProductsByPurchase(option.id);
            }}
            error={errors.purchaseRefId}
            required
          />
        </div>

        <div className="md:col-span-2">
          <SimpleSearchSelect
            label="Producto del CCF que Afecta"
            name="productId"
            iconKey="product"
            options={productOptions}
            selectedValue={form.productId ? Number(form.productId) : null}
            onSelect={(option: any) => {
              console.log('Producto seleccionado:', option);
              setForm((prev) => ({
                ...prev,
                productId: option.id,
                productName: option.name,
                unitCost: option.unitCost?.toString() || '',
              }));
            }}
            error={errors.productId}
            required
          />
        </div>
      </div>

      {/* Cantidad */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <InputField
          label="Cantidad"
          name="quantity"
          type="number"
          iconKey="product"
          value={form.quantity}
          onChange={handleChange}
          error={errors.quantity}
          required
        />
      </div>

      {/* Costo y totales */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <InputField
          label="Costo Unitario"
          name="unitCost"
          type="number"
          iconKey="dollar"
          value={form.unitCost}
          onChange={handleChange}
          error={errors.unitCost}
          required
        />

        <InputField
          label="IVA Total"
          name="ivaTotal"
          type="number"
          iconKey="dollar"
          value={form.ivaTotal}
          onChange={() => {}}
          disabled
        />

        <InputField
          label="Costo Total"
          name="totalCost"
          type="number"
          iconKey="dollar"
          value={form.totalCost}
          onChange={() => {}}
          disabled
        />
      </div>

      {/* Totales individuales */}
      <div className="text-gray-600 space-y-1 text-sm dark:text-white">
        <p>CON IVA: $ {form.withIva || '0.00000000'}</p>
        <p>IVA UNITARIO: $ {form.ivaUnit || '0.00000000'}</p>
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddReturnProductForm;
